<script setup>
import dayjs from 'dayjs'
import { getI18n } from '@/plugins/i18n'
import { ref, computed, onMounted } from 'vue'

import { usePlanningStore } from '@/stores/planning.store'
import { useWebSiteStore, searchResultTypeEnum } from '@/stores/web-site.store'
import { useChatStore } from '@/stores/chat.store'
import { useNavigator } from '@/composables/navigator'
import { useArchive } from '@/composables/archive'
import { useChips } from '@/composables/chips'
import { useDetectDevice } from '@/composables/device'

import ArchiveWarningDialog from '@/components/shared/ArchiveWarningDialog.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import ListItem from '@/components/shared/ListItem.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import CollapsibleDetailHeader from '@/components/shared/CollapsibleDetailHeader.vue'
import CompanyEdit from '@/components/shared/CompanyEdit.vue'
import ContactListItem from '@/components/shared/ContactListItem.vue'
import ContactDetail from '@/components/shared/ContactDetail.vue'
import ContactEdit from '@/components/shared/ContactEdit.vue'
import CompanyRoi from '@/components/planner/CompanyRoi.vue'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useEntityChanges } from '@/composables/entity-changes'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})
useChatStore().trackPageEntity(searchResultTypeEnum.COMPANY, () => props.modelValue?.id)

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const editItem = ref(null)
const company = ref(props.modelValue || null)
const companyStatistics = ref(null)
const contacts = ref([])
const selectedContact = ref(null)
const contactEditItem = ref(null)

const onContactDetailUpdate = async() => {
  selectedContact.value = null
  await updateDataAsync()
}

const onAddContact = () => {
  contactEditItem.value = { company: company.value.id, references: [], is_default: false }
}

const onContactEditChanged = async() => {
  contactEditItem.value = null
  await updateDataAsync()
}
const { getCompanyChips } = useChips()

const updateDataAsync = async() => {
  company.value = (await planningStore.fetchCompanyByIdAsync(company.value.id, { all: true })).data
  if (webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)) companyStatistics.value = (await planningStore.fetchCompanyStatisticsByIdAsync(company.value.id)).data
  contacts.value = (await planningStore.fetchContactsByCompanyAsync(company.value.id)).data
}

onMounted(async() => {
  if (!props.modelValue?.id) return
  await updateDataAsync()
})

const companyStatisticsLabel = computed(() => {
  const stats = companyStatistics.value
  if (!stats || stats.total_projects === 0) return i18n.t('common.companyStatistics.noData')
  return i18n.t('common.companyStatistics.data', { ...stats, first_project_date: dayjs(stats.first_project_date).format('L') })
})

let changed = false
const onNavigateBack = () => (changed ? emit('update') : emit('cancel'))
const emit = defineEmits(['cancel', 'update'])
useNavigator(onNavigateBack)

const { archiveWarning, archiveAsync } = useArchive({
  onArchive: async(id) => { await planningStore.archiveCompanyAsync(id) },
  onDelete: async(id, config) => { await planningStore.deleteCompanyAsync(id, config) },
})

const onArchive = async(item) => {
  await archiveAsync(item.id, item.archived)
  item.archived = !item.archived
}

const reloadCompanyAsync = async() => {
  await updateDataAsync()
  changed = true
}

const onEditItemUpdate = async() => {
  await reloadCompanyAsync()
  editItem.value = null
}

useEntityChanges([entityChangeTypeEnum.COMPANY], (change) => change.id === company.value?.id && reloadCompanyAsync(), () => editItem.value !== null)
</script>

<template>
  <q-card v-if="company" class="column full-height">
    <q-dialog
      :model-value="editItem !== null"
      :maximized="isMobile"
      persistent
      class="dialog-xs"
    >
      <company-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onEditItemUpdate"
        @cancel="onEditItemUpdate"
        @delete="editItem = null; emit('update')"
      />
    </q-dialog>
    <archive-warning-dialog
      v-model="archiveWarning"
      @confirm="async() => await archiveAsync(archiveWarning.item.id, false)"
    />
    <q-dialog maximized :model-value="selectedContact !== null" @hide="selectedContact = null">
      <contact-detail
        v-if="selectedContact !== null"
        :model-value="selectedContact"
        @cancel="selectedContact = null"
        @update="onContactDetailUpdate"
      />
    </q-dialog>
    <q-dialog
      :maximized="isMobile"
      :model-value="contactEditItem !== null"
      class="dialog-xs"
      @hide="contactEditItem = null"
    >
      <contact-edit
        v-if="contactEditItem !== null"
        :model-value="contactEditItem"
        @update="onContactEditChanged"
        @cancel="contactEditItem = null"
      />
    </q-dialog>
    <navigate-back
      :text="company.name"
      :archived="company.archived"
      @cancel="onNavigateBack"
    >
      <template #right>
        <div style="display: flex; align-items: center;">
          <q-btn
            v-show="!company.archived"
            flat
            round
            icon="mdi-pencil"
            @click="editItem = company"
          />
          <q-btn
            flat
            round
            :icon="company?.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'"
            @click="onArchive(company)"
          />
        </div>
      </template>
    </navigate-back>

    <q-card-section class="col scroll q-pa-sm" style="min-height: 0;">
      <collapsible-detail-header
        v-model:compact="webSiteStore.companyDetailHeaderCompact"
        collapsible
        :avatar="company?.avatar_file || company?.avatar_url"
        avatar-icon="mdi-camera-off"
      >
        <template #chips>
          <chip-template :values="getCompanyChips(company)" size="md" />
        </template>
      </collapsible-detail-header>
      <template v-if="webSiteStore.can(webSiteStore.permissionFlagEnum.FINANCIALS)">
        <list-item
          avatar-icon="mdi-chart-box"
          :label="companyStatisticsLabel"
        />
        <company-roi :company="company" variant="list" />
      </template>
      <q-separator class="q-my-sm" />
      <div class="row items-center justify-between q-px-sm">
        <div class="text-subtitle2 text-grey-7">{{ i18n.t('common.contact', 2) }}</div>
        <q-btn
          round
          flat
          size="1rem"
          style="margin: 0;"
          icon="mdi-plus-circle-outline"
          @click="onAddContact"
        />
      </div>
      <q-list v-if="contacts.length">
        <q-item
          v-for="contact in contacts"
          :key="contact.id"
          clickable
          class="q-pa-sm"
          @click="selectedContact = contact"
        >
          <contact-list-item :contact="contact" />
        </q-item>
      </q-list>
    </q-card-section>
  </q-card>
</template>
